import React, { useState, useRef, Children } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { LinkedinIcon, GlobeIcon, AwardIcon, BriefcaseIcon, GraduationCapIcon, LeafIcon, UsersIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { MAILTO } from '../utils/mailto';
const IVAN_PHOTO = 'https://cdn.magicpatterns.com/uploads/t49pLg5FC657EpgAgGe33M/image.png';
const TEAM_MEMBERS = [{
  photo: 'https://agx-lab.com/lovable-uploads/maria3.png',
  initials: 'M',
  name: 'Team Member 1',
  role: 'Engineer'
}, {
  photo: 'https://agx-lab.com/lovable-uploads/80e28e44-3cd1-44cb-8fd5-a0b402f6078e.png',
  initials: 'M2',
  name: 'Team Member 2',
  role: 'Engineer'
}, {
  photo: 'https://agx-lab.com/lovable-uploads/107ee99c-7ec4-485f-9af8-1e77b363ed3f.png',
  initials: 'M3',
  name: 'Team Member 3',
  role: 'Engineer'
}, {
  photo: 'https://agx-lab.com/lovable-uploads/grace.jpeg',
  initials: 'G',
  name: 'Team Member 4',
  role: 'Engineer'
}, {
  photo: 'https://agx-lab.com/lovable-uploads/397d5704-034a-479c-9d00-b26f5c2282e0.png',
  initials: 'M5',
  name: 'Team Member 5',
  role: 'Engineer'
}];
function AboutHero() {
  return <section className="relative w-full overflow-hidden bg-white" data-id="element-1088">
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" aria-hidden="true" data-id="element-1089">
        <defs data-id="element-1090">
          <pattern id="about-grid" width="50" height="50" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-1091">
            <line x1="0" y1="0" x2="0" y2="50" stroke="#1B4332" strokeWidth="0.5" data-id="element-1092" />
            <line x1="0" y1="0" x2="50" y2="0" stroke="#1B4332" strokeWidth="0.5" data-id="element-1093" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#about-grid)" data-id="element-1094" />
      </svg>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32" data-id="element-1095">
        <motion.div className="text-center" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1096">
          <p className="text-sm font-semibold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-1097">
            About CarbonSafe
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-1098">
            Technology-first climate
            <span className="text-carbon-bright" data-id="element-1099"> partners</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-carbon-mid max-w-2xl mx-auto leading-relaxed" data-id="element-1100">
            We bridge the gap between complex digital systems and sustainable
            real-world applications, turning purpose into profit across Uganda's
            green economy.
          </p>
        </motion.div>
      </div>
    </section>;
}
function CEOSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [showHighlights, setShowHighlights] = useState(false);
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-1101">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1102">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16" data-id="element-1103">
          {/* Photo & Quick Info */}
          <motion.div className="lg:col-span-2" initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1104">
            <div className="sticky top-28" data-id="element-1105">
              <div className="relative" data-id="element-1106">
                <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden bg-carbon-geo" data-id="element-1107">
                  <img src={IVAN_PHOTO} alt="Ivan Kahangire, Founder & CEO of CarbonSafe" className="w-full h-full object-cover object-top" data-id="element-1108" />
                </div>
                {/* Accent bar */}
                <div className="absolute -bottom-3 -right-3 w-24 h-24 rounded-xl bg-carbon-bright/10 -z-10" aria-hidden="true" data-id="element-1109" />
                <div className="absolute -top-3 -left-3 w-16 h-16 rounded-lg bg-carbon-deep/5 -z-10" aria-hidden="true" data-id="element-1110" />
              </div>

              <div className="mt-8" data-id="element-1111">
                <h2 className="text-2xl font-bold text-carbon-deep" data-id="element-1112">
                  Ivan Kahangire
                </h2>
                <p className="text-carbon-bright font-semibold mt-1" data-id="element-1113">
                  Founder & CEO
                </p>

                <div className="mt-4 flex flex-wrap gap-2" data-id="element-1114">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-white text-xs font-medium text-carbon-deep" data-id="element-1115">
                    <AwardIcon className="w-3 h-3 text-carbon-bright" data-id="element-1116" />
                    ISO 14064 Certified
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-white text-xs font-medium text-carbon-deep" data-id="element-1117">
                    <UsersIcon className="w-3 h-3 text-carbon-bright" data-id="element-1118" />
                    Founder Institute
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-carbon-geo bg-white text-xs font-medium text-carbon-deep" data-id="element-1119">
                    <LeafIcon className="w-3 h-3 text-carbon-bright" data-id="element-1120" />
                    Green Economy
                  </span>
                </div>

                <div className="mt-5 flex gap-3" data-id="element-1121">
                  <a href="mailto:operations@carbonsafe.earth" className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright hover:bg-carbon-bright/10 transition-colors" aria-label="LinkedIn" data-id="element-1122">
                    <LinkedinIcon className="w-4 h-4" data-id="element-1123" />
                  </a>
                  <a href="mailto:operations@carbonsafe.earth" className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-bright hover:bg-carbon-bright/10 transition-colors" aria-label="Website" data-id="element-1124">
                    <GlobeIcon className="w-4 h-4" data-id="element-1125" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bio Content */}
          <motion.div className="lg:col-span-3" initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-1126">
            <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-1127">
              Leadership
            </span>

            <blockquote className="text-xl sm:text-2xl font-bold text-carbon-deep leading-snug border-l-4 border-carbon-bright pl-5 mb-8" data-id="element-1128">
              "Digital transformation should drive environmental resilience. We
              monetize purpose, turning sustainable ideas into scalable business
              ventures."
            </blockquote>

            <p className="text-base text-carbon-mid leading-relaxed mb-6" data-id="element-1129">
              As the Founder and Team Lead of CarbonSafe, Ivan Kahangire's
              career represents a strategic evolution from enterprise technology
              and data science into the heart of Uganda's green economy. His
              professional journey is defined by his ability to bridge the gap
              between complex digital systems and sustainable real-world
              applications.
            </p>

            <p className="text-base text-carbon-mid leading-relaxed mb-8" data-id="element-1130">
              Ivan's career is anchored in the belief that digital
              transformation should drive environmental resilience. His
              leadership at CarbonSafe reflects a "Value Addition" mindset,
              moving beyond raw data to create high-integrity, verifiable carbon
              assets.
            </p>

            {/* Career Highlights Link */}
            <button onClick={() => setShowHighlights(true)} className="inline-flex items-center gap-2 text-carbon-bright font-semibold text-base hover:text-carbon-light transition-colors group mb-10" data-id="element-1131">
              <BriefcaseIcon className="w-4.5 h-4.5" data-id="element-1132" />
              View Career Highlights
              <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-1133" />
            </button>

            {/* Closing quote */}
            <div className="p-6 rounded-2xl bg-carbon-deep/[0.03] border border-carbon-geo" data-id="element-1134">
              <p className="text-sm text-carbon-mid leading-relaxed italic" data-id="element-1135">
                "Ivan's career trajectory, from managing global tech accounts to
                empowering rural farmers, ensures that CarbonSafe remains both
                technically sophisticated and locally grounded."
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Career Highlights Modal */}
      <AnimatePresence data-id="element-1136">
        {showHighlights && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setShowHighlights(false)} data-id="element-1137" />

            {/* Modal */}
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1138">
              <motion.div className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto" initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-1139">
                {/* Modal Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between px-6 sm:px-8 py-5 bg-white border-b border-carbon-geo" data-id="element-1140">
                  <div data-id="element-1141">
                    <h3 className="text-lg font-bold text-carbon-deep" data-id="element-1142">
                      Career Highlights
                    </h3>
                    <p className="text-sm text-carbon-mid mt-0.5" data-id="element-1143">
                      Ivan Kahangire, Founder & CEO
                    </p>
                  </div>
                  <button onClick={() => setShowHighlights(false)} className="w-9 h-9 rounded-lg bg-carbon-deep/5 flex items-center justify-center text-carbon-mid hover:text-carbon-deep hover:bg-carbon-deep/10 transition-colors" aria-label="Close" data-id="element-1144">
                    <XIcon className="w-5 h-5" data-id="element-1145" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="px-6 sm:px-8 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-1146">
                  <div className="space-y-6" data-id="element-1147">
                    <div className="flex gap-4" data-id="element-1148">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-1149">
                        <BriefcaseIcon className="w-5 h-5 text-carbon-bright" data-id="element-1150" />
                      </div>
                      <div data-id="element-1151">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1152">
                          Technology & Data Leadership
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-1153">
                          Established himself as a Digital Transformation
                          Specialist and Data Scientist. Served as Territory
                          Manager for Oracle in East Africa, General Manager at
                          Computech Ltd, and Country Manager at DataGroupIT,
                          mastering Cyber Security, Cloud Computing, and
                          Enterprise Infrastructure that now power CarbonSafe's
                          dMRV capabilities.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-1154">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-1155">
                        <LeafIcon className="w-5 h-5 text-carbon-bright" data-id="element-1156" />
                      </div>
                      <div data-id="element-1157">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1158">
                          The Founder Spirit
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-1159">
                          CEO of Kahangire Farm Ltd, where he applied early
                          "AgTech" principles to modern farming. This hands-on
                          experience in agricultural production provided the
                          foundation for CarbonSafe's focus on soil carbon and
                          smallholder farmer integration.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-1160">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-1161">
                        <UsersIcon className="w-5 h-5 text-carbon-bright" data-id="element-1162" />
                      </div>
                      <div data-id="element-1163">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1164">
                          Ecosystem Architect
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-1165">
                          Recognized as a prominent mentor in the Ugandan
                          startup scene, Ivan serves as Co-Director at the
                          Founder Institute Kampala, graduating new waves of
                          technology companies and championing those in the
                          Green and Circular Economy.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-1166">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-1167">
                        <GraduationCapIcon className="w-5 h-5 text-carbon-bright" data-id="element-1168" />
                      </div>
                      <div data-id="element-1169">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1170">
                          Academic Foundation
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-1171">
                          A graduate of Bangalore University (India) and
                          Coventry University (UK), Ivan's academic background
                          in ICT and Business has allowed him to navigate the
                          intersection of technical engineering and strategic
                          market expansion.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-1172">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-carbon-bright/10 flex items-center justify-center mt-0.5" data-id="element-1173">
                        <AwardIcon className="w-5 h-5 text-carbon-bright" data-id="element-1174" />
                      </div>
                      <div data-id="element-1175">
                        <h4 className="text-sm font-bold text-carbon-deep" data-id="element-1176">
                          ISO 14064 Certified
                        </h4>
                        <p className="text-sm text-carbon-mid leading-relaxed mt-1" data-id="element-1177">
                          Certified in Green House Gases standards, ensuring
                          CarbonSafe's verification processes meet the highest
                          international benchmarks for carbon accounting and
                          reporting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
}
function TeamSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const members = [{
    photo: 'https://agx-lab.com/lovable-uploads/maria3.png',
    name: 'Maria N.',
    role: 'Chief Technology Officer',
    bio: "Former cloud architect at AWS East Africa. Leads CarbonSafe's IoT infrastructure, satellite data pipelines, and dMRV platform engineering."
  }, {
    photo: 'https://agx-lab.com/lovable-uploads/80e28e44-3cd1-44cb-8fd5-a0b402f6078e.png',
    name: 'David M.',
    role: 'Head of Data Science',
    bio: "PhD in Environmental Remote Sensing from Kenyatta University. Develops the machine learning models that power CarbonSafe's emission quantification algorithms."
  }, {
    photo: 'https://agx-lab.com/lovable-uploads/107ee99c-7ec4-485f-9af8-1e77b363ed3f.png',
    name: 'Sarah K.',
    role: 'Head of Operations',
    bio: '10+ years in supply chain management across East Africa. Oversees field operations, cookstove distribution networks, and farmer onboarding programs.'
  }, {
    photo: 'https://agx-lab.com/lovable-uploads/grace.jpeg',
    name: 'Grace M.',
    role: 'Lead Software Engineer',
    bio: 'Full-stack engineer with expertise in geospatial systems and dMRV infrastructure. Builds the real-time dashboards and mobile tools that field teams rely on daily.'
  }, {
    photo: 'https://agx-lab.com/lovable-uploads/397d5704-034a-479c-9d00-b26f5c2282e0.png',
    name: 'Patricia N.',
    role: 'Head of Partnerships',
    bio: 'Manages relationships with carbon credit buyers, NGOs, and government regulatory bodies across East Africa and Europe.'
  }, {
    photo: 'https://agx-lab.com/lovable-uploads/a358774d-0879-432c-a0c9-9b963478868a.png',
    name: 'Harshid P.',
    role: 'Engineer',
    bio: "Brings deep technical expertise to CarbonSafe's platform, contributing to the engineering systems that power Uganda's leading carbon accounting infrastructure."
  },
  // --- Added new team member as per comment ---
  {
    photo: 'https://agx-lab.com/lovable-uploads/newmember.png',
    name: 'Leon K.',
    role: 'Head of Operations',
    bio: 'Responsible for overseeing operational activities, optimizing logistics, and ensuring sustainable practices across all projects.'
  }];
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28" data-id="element-1178">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1179">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1180">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-1181">
            The team behind{' '}
            <span className="text-carbon-bright" data-id="element-1182">CarbonSafe</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-1183">
            A multidisciplinary team of engineers, scientists, and
            sustainability experts building Africa's most trusted carbon
            verification platform.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-1184">
          {members.map(member => <motion.article key={member.name} className="rounded-2xl border border-carbon-geo bg-white overflow-hidden" variants={cardVariants} data-id="element-1185">
              <div className="aspect-square overflow-hidden bg-carbon-geo" data-id="element-1186">
                <img src={member.photo} alt={member.name} className="w-full h-full object-cover object-top grayscale" data-id="element-1187" />
              </div>
              <div className="p-6" data-id="element-1188">
                <h3 className="text-lg font-bold text-carbon-deep" data-id="element-1189">
                  {member.name}
                </h3>
                <p className="text-sm font-semibold text-carbon-bright mt-0.5" data-id="element-1190">
                  {member.role}
                </p>
                <p className="text-sm text-carbon-mid leading-relaxed mt-3" data-id="element-1191">
                  {member.bio}
                </p>
              </div>
            </motion.article>)}
        </motion.div>
      </div>
    </section>;
}
function AboutCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full py-20 sm:py-28 bg-white" data-id="element-1192">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1193">
        <motion.div className="relative overflow-hidden rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center" style={{
        background: 'linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)'
      }} initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1194">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #52B788, transparent)'
        }} aria-hidden="true" data-id="element-1195" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-10" style={{
          background: 'radial-gradient(circle, #6BCB77, transparent)'
        }} aria-hidden="true" data-id="element-1196" />
          <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight max-w-3xl mx-auto" data-id="element-1197">
            Join our mission to build
            <br data-id="element-1198" />
            <span className="text-carbon-bright" data-id="element-1199">Africa's carbon future</span>
          </h2>
          <p className="relative mt-5 text-lg text-white/70 max-w-xl mx-auto" data-id="element-1200">
            We're always looking for passionate people who want to make a real
            impact on climate action in East Africa.
          </p>
          <div className="relative mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1201">
            <a href={MAILTO.careers} className="inline-flex items-center gap-2 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-black/20" data-id="element-1202">
              View Open Roles
              <ArrowRightIcon className="w-4 h-4" data-id="element-1203" />
            </a>
            <a href={MAILTO.aboutContact} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-lg hover:bg-white/10 transition-colors" data-id="element-1204">
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}
export function About() {
  return <>
      <AboutHero data-id="element-1205" />
      <CEOSection data-id="element-1206" />
      <TeamSection data-id="element-1207" />
      <AboutCTA data-id="element-1208" />
      <Footer data-id="element-1209" />
    </>;
}